/**
 * Optional hook wrappers for posture analysis.
 *
 * In self-assessment mode the component tree does NOT include
 * ClientProvider, AdminProvider, SettingsProvider, etc.
 * Calling their hooks directly would throw "must be used within a …Provider".
 *
 * These wrappers use `useContext` directly against the raw context objects
 * (which return `undefined` when no provider is present) instead of calling
 * the original hooks (which throw on missing provider).
 *
 * This approach is Vite-compatible (no `require()`), React Rules-of-Hooks
 * compliant (useContext is always called), and returns safe defaults when
 * the provider is absent.
 */

import { useContext } from 'react';
import type { Client } from '@/lib/types';
import { ClientContext } from '@/context/client-context';
import { AdminContext } from '@/contexts/AdminContext';
import { SettingsContext } from '@/components/settings/lib/settings-context';
import { AuthContext } from '@/contexts/AuthContext';

// ---------------------------------------------------------------------------
// Optional hook wrappers
// ---------------------------------------------------------------------------

/**
 * Returns `{ client, setClient, isClientLoading }` when inside a
 * `<ClientProvider>`, or safe defaults otherwise.
 */
export function useOptionalClient(): {
  client: Client | null;
  setClient: (c: Client | null) => void;
  isClientLoading: boolean;
} {
  const ctx = useContext(ClientContext);
  if (!ctx) return { client: null, setClient: () => {}, isClientLoading: false };
  return ctx;
}

/**
 * Returns the admin context when inside `<AdminProvider>`, or safe defaults.
 */
export function useOptionalAdmin(): {
  isAdminMode: boolean;
  isAuthorizedAdmin: boolean;
} {
  const ctx = useContext(AdminContext);
  if (!ctx) return { isAdminMode: false, isAuthorizedAdmin: false };
  return ctx;
}

/**
 * Returns the settings context when inside `<SettingsProvider>`, or safe defaults.
 */
export function useOptionalSettings(): {
  settings: Record<string, any>;
  setSettings: (fn: (prev: any) => any) => void;
  isLoading: boolean;
} {
  const ctx = useContext(SettingsContext);
  if (!ctx) {
    return {
      settings: {},
      setSettings: () => {},
      isLoading: false,
    };
  }
  return ctx;
}

/**
 * Returns the scoped Firestore DB when inside `<AuthProvider>` with active session,
 * or `null` otherwise.
 */
export function useOptionalScopedDb() {
  const ctx = useContext(AuthContext);
  return ctx?.scopedDb ?? null;
}

/**
 * Returns the auth user when inside `<AuthProvider>`, or safe defaults.
 */
export function useOptionalAuth(): { user: any | null; loading: boolean } {
  const ctx = useContext(AuthContext);
  if (!ctx) return { user: null, loading: false };
  return { user: ctx.user, loading: ctx.loading };
}

